import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';

/**
 * GuidedTour — generic, dependency-free spotlight tour primitive.
 *
 * MODULE-AGNOSTIC: this file must never import anything module-specific
 * (Screener, Watchlist, ...). Each module supplies its own step catalog and
 * a thin controller; see components/Screener/ScreenerTourController.js for
 * the reference wiring.
 *
 * Step shape:
 *   {
 *     id: string,                  // stable id
 *     target: string | (() => string) | null,
 *                                  // CSS selector (usually [data-tour="..."]); a function is
 *                                  // re-evaluated per poll tick (dynamic columns); null with placement:'center'
 *     title: string,
 *     body: string | string[] | (() => string|string[]),
 *                                  // paragraphs; a function is evaluated at step activation
 *                                  // (after target resolution) so copy can quote live DOM values
 *     prepare?: () => void|Promise // idempotent setup (expand accordion, switch view) before resolving target
 *     placement?: 'auto' | 'center'
 *     nextLabel?: string           // override the Next/Finish button text (e.g. "Run scan")
 *     onNext?: () => void|Promise  // fired on Next/Finish click, before advancing (best-effort)
 *     extraTargets?: string[] | (() => string[])
 *                                  // additional selectors highlighted with this step: the dim
 *                                  // cutout becomes the union box of target + extras, and every
 *                                  // resolved element gets its own accent ring. Best-effort —
 *                                  // missing extras are ignored.
 *     choices?: Array<{key, label, description?, selected?, onSelect}> | (() => Array<...>)
 *                                  // option buttons rendered in the card between body and footer.
 *                                  // Clicking a choice calls its onSelect() then auto-advances;
 *                                  // Next stays visible and means "keep the current selection".
 *   }
 *
 * Behavior:
 * - Poll for the target (100ms x 15). Missing target => auto-skip in the
 *   current navigation direction; if nothing remains, finish.
 * - Fixed full-screen overlay (z-index 1400, above theme notification:1300)
 *   blocks all page interaction; the tooltip card is the only interactive UI.
 * - Spotlight cutout uses the box-shadow trick and transitions between steps.
 * - Escape = skip. Recomputes geometry on resize + capture-phase scroll.
 */

const OVERLAY_Z = 1400;
const CARD_WIDTH = 340;
const PAD = 8; // spotlight padding around the target rect
const POLL_INTERVAL = 100;
const POLL_MAX_TRIES = 15;
const SETTLE_MS = 350; // wait after scrollIntoView before measuring

// ---------------------------------------------------------------------------
// Generic prepare-helper utilities (exported for module step catalogs)
// ---------------------------------------------------------------------------

/** Click `clickSel` inside/at `rootSel` unless `expandedSel` already exists. */
export function expandBySelector(rootSel, clickSel, expandedSel) {
  const root = document.querySelector(rootSel);
  if (!root) return;
  if (expandedSel && root.querySelector(expandedSel)) return;
  const clickable = clickSel ? root.querySelector(clickSel) : root;
  if (clickable) clickable.click();
}

/** Click the element at `sel` if it doesn't carry `activeClass`. Returns true if it clicked. */
export function ensureActiveBySelector(sel, activeClass) {
  const el = document.querySelector(sel);
  if (!el) return false;
  if (activeClass && el.classList.contains(activeClass)) return false;
  el.click();
  return true;
}

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

async function resolveTarget(step) {
  if (!step.target) return null; // centered step, no anchor
  for (let i = 0; i < POLL_MAX_TRIES; i++) {
    // Function targets are re-evaluated every tick so selectors that depend on
    // late-mounting DOM (dynamic table columns) can resolve once it appears.
    let sel;
    try {
      sel = typeof step.target === 'function' ? step.target() : step.target;
    } catch (e) {
      sel = null;
    }
    const el = sel ? document.querySelector(sel) : null;
    if (el) return el;
    await wait(POLL_INTERVAL);
  }
  return undefined; // signal: not found
}

// Resolve a step's extraTargets to elements, best-effort (missing → dropped).
function resolveExtras(step) {
  let sels = step.extraTargets;
  if (typeof sels === 'function') {
    try {
      sels = sels();
    } catch (e) {
      sels = [];
    }
  }
  if (!Array.isArray(sels)) return [];
  return sels
    .map((s) => {
      try {
        return s ? document.querySelector(s) : null;
      } catch (e) {
        return null;
      }
    })
    .filter(Boolean);
}

// Evaluate a step body (static value or live-DOM function) safely.
function resolveBody(step) {
  if (typeof step.body !== 'function') return step.body;
  try {
    return step.body();
  } catch (e) {
    return '';
  }
}

// ---------------------------------------------------------------------------
// GuideButton — small pill button to (re)launch a tour. Mirrors the styling
// of the existing WalkthroughButton so they sit side by side naturally.
// ---------------------------------------------------------------------------
export function GuideButton({ onClick, label = 'Guide' }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: 'transparent',
        border: '1px solid #444',
        color: '#aeb9cc',
        fontSize: '12px',
        padding: '4px 12px',
        borderRadius: '20px',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '5px',
        transition: 'border-color 0.2s ease, color 0.2s ease',
        flexShrink: 0,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = '#218aff';
        e.currentTarget.style.color = '#218aff';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = '#444';
        e.currentTarget.style.color = '#aeb9cc';
      }}
      aria-label={`Start ${label.toLowerCase()} tour`}
    >
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
        <circle cx="12" cy="12" r="9" />
        <line x1="12" y1="11" x2="12" y2="17" />
        <circle cx="12" cy="7.5" r="0.5" fill="currentColor" />
      </svg>
      {label}
    </button>
  );
}

// ---------------------------------------------------------------------------
// GuidedTour engine
// ---------------------------------------------------------------------------
export default function GuidedTour({
  steps,
  isActive,
  startIndex = 0,
  onFinish,
  onSkip,
  onStepChange,
}) {
  const [index, setIndex] = useState(startIndex);
  const [rect, setRect] = useState(null); // cutout rect (union of target + extras) or null (centered)
  const [rings, setRings] = useState([]); // per-element accent rings (only when extras exist)
  const [resolvedBody, setResolvedBody] = useState(null); // body evaluated at activation
  const [resolving, setResolving] = useState(false);
  const targetElRef = useRef(null);
  const extraElsRef = useRef([]);
  // Real rendered card height — measured after every content/position change so
  // placement never relies on an estimate (a too-small estimate previously let
  // tall cards hang off the bottom of the viewport with unreachable buttons).
  const cardRef = useRef(null);
  const [cardH, setCardH] = useState(220);
  const activeRef = useRef(isActive);
  activeRef.current = isActive;
  // Monotonic token so a stale async resolution can't clobber a newer step.
  const resolveTokenRef = useRef(0);

  const step = steps[index] || null;
  const total = steps.length;

  const measure = useCallback(() => {
    const el = targetElRef.current;
    if (!el || !document.body.contains(el)) {
      setRect(null);
      setRings([]);
      return;
    }
    const els = [el, ...extraElsRef.current.filter((x) => x && document.body.contains(x))];
    const rects = els.map((x) => x.getBoundingClientRect());
    const top = Math.min(...rects.map((r) => r.top));
    const left = Math.min(...rects.map((r) => r.left));
    const right = Math.max(...rects.map((r) => r.right));
    const bottom = Math.max(...rects.map((r) => r.bottom));
    setRect({ top, left, width: right - left, height: bottom - top });
    // Individual rings only make sense when there is more than one element —
    // a lone target keeps the plain spotlight look.
    setRings(
      els.length > 1
        ? rects.map((r) => ({ top: r.top, left: r.left, width: r.width, height: r.height }))
        : []
    );
  }, []);

  // Activate a step: prepare -> resolve -> scroll -> measure. Auto-skips in
  // `dir` (+1/-1) when the anchor never appears.
  const activateStep = useCallback(async (i, dir) => {
    const token = ++resolveTokenRef.current;
    if (i < 0) { i = 0; dir = 1; }
    while (i >= 0 && i < steps.length) {
      const s = steps[i];
      setResolving(true);
      try {
        if (s.prepare) await s.prepare();
      } catch (e) {
        // prepare is best-effort; a failed prepare falls through to resolution
      }
      const el = await resolveTarget(s);
      if (token !== resolveTokenRef.current || !activeRef.current) return;
      if (el === null) {
        // Centered step — no target
        targetElRef.current = null;
        extraElsRef.current = [];
        setRect(null);
        setRings([]);
        setResolvedBody(resolveBody(s));
        setIndex(i);
        setResolving(false);
        if (onStepChange) onStepChange(i, s);
        return;
      }
      if (el !== undefined) {
        try {
          el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        } catch (e) {
          el.scrollIntoView();
        }
        await wait(SETTLE_MS);
        if (token !== resolveTokenRef.current || !activeRef.current) return;
        targetElRef.current = el;
        extraElsRef.current = resolveExtras(s);
        setResolvedBody(resolveBody(s));
        measure();
        setIndex(i);
        setResolving(false);
        if (onStepChange) onStepChange(i, s);
        return;
      }
      // Anchor not found — skip in the navigation direction
      i += dir;
    }
    // Walked off either end without resolving a step
    setResolving(false);
    if (activeRef.current && onFinish) onFinish();
  }, [steps, onFinish, onStepChange, measure]);

  // (Re)start when the tour becomes active
  useEffect(() => {
    if (isActive) {
      activateStep(startIndex, 1);
    } else {
      resolveTokenRef.current++;
      targetElRef.current = null;
      extraElsRef.current = [];
      setRect(null);
      setRings([]);
      setResolvedBody(null);
      setIndex(startIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  // Track geometry on resize / scroll (rAF-throttled)
  useEffect(() => {
    if (!isActive) return;
    let raf = null;
    const onMove = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        measure();
      });
    };
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => {
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [isActive, measure]);

  // Measure the card whenever its content or anchor changes so placement math
  // uses the real height (runs before paint — no visible jump). The cardH
  // guard makes this settle in one extra pass instead of looping.
  useLayoutEffect(() => {
    if (!isActive) return;
    if (cardRef.current) {
      const h = cardRef.current.offsetHeight;
      if (h && h !== cardH) setCardH(h);
    }
  }, [isActive, index, resolvedBody, rect, rings, cardH]);

  const handleSkip = useCallback(() => {
    if (onSkip) onSkip();
  }, [onSkip]);

  // Escape = skip
  useEffect(() => {
    if (!isActive) return;
    const onKey = (e) => {
      if (e.key === 'Escape') handleSkip();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isActive, handleSkip]);

  if (!isActive || !step) return null;

  const isLast = index === total - 1;

  const handleNext = async () => {
    if (step.onNext) {
      try {
        await step.onNext();
      } catch (e) {
        // onNext is best-effort — a failed action never blocks the tour
      }
    }
    if (isLast) {
      if (onFinish) onFinish();
    } else {
      activateStep(index + 1, 1);
    }
  };

  const handleBack = () => {
    if (index > 0) activateStep(index - 1, -1);
  };

  // --- Geometry ---
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const centered = step.placement === 'center' || !rect;

  let cutout = null;
  if (!centered && rect) {
    cutout = {
      top: rect.top - PAD,
      left: rect.left - PAD,
      width: rect.width + PAD * 2,
      height: rect.height + PAD * 2,
    };
  }

  // Card position: below target, flip above if clipped, clamp to viewport.
  const cardMaxWidth = Math.min(CARD_WIDTH, vw - 24);
  let cardStyle;
  if (centered || !cutout) {
    cardStyle = {
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
    };
  } else {
    // Use the MEASURED card height: below if it fits, else above, else pinned
    // fully inside the viewport (possibly overlapping the spotlight) so the
    // Back/Next/Skip controls are always reachable.
    const below = cutout.top + cutout.height + 12;
    let top;
    if (below + cardH + 12 <= vh) {
      top = below;
    } else if (cutout.top - cardH - 12 >= 12) {
      top = cutout.top - cardH - 12;
    } else {
      top = Math.max(12, vh - cardH - 12);
    }
    let left = cutout.left + cutout.width / 2 - cardMaxWidth / 2;
    left = Math.max(12, Math.min(left, vw - cardMaxWidth - 12));
    cardStyle = { top, left };
  }

  const bodyRaw = resolvedBody != null
    ? resolvedBody
    : (typeof step.body === 'function' ? '' : step.body);
  const bodyLines = (Array.isArray(bodyRaw) ? bodyRaw : [bodyRaw]).filter(Boolean);

  // Choice buttons (evaluated at render so `selected` reflects live state)
  let choiceList = [];
  if (step.choices) {
    try {
      choiceList = typeof step.choices === 'function' ? step.choices() : step.choices;
    } catch (e) {
      choiceList = [];
    }
    if (!Array.isArray(choiceList)) choiceList = [];
  }

  const handleChoice = async (choice) => {
    try {
      if (choice.onSelect) await choice.onSelect();
    } catch (e) {
      // best-effort — a failed selection never blocks the tour
    }
    // Let any state change from onSelect commit (e.g. a mode switch swapping
    // the form behind the overlay) before the next step prepares/resolves.
    await wait(350);
    if (isLast) {
      if (onFinish) onFinish();
    } else {
      activateStep(index + 1, 1);
    }
  };

  return ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: OVERLAY_Z,
        // Overlay itself is transparent; the dimming comes from the cutout's
        // giant box-shadow (or a full dim layer for centered steps). It still
        // captures all pointer events so the page underneath is inert.
        background: centered ? 'rgba(0,0,0,0.65)' : 'transparent',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Spotlight cutout */}
      {!centered && cutout && (
        <div
          style={{
            position: 'absolute',
            top: cutout.top,
            left: cutout.left,
            width: cutout.width,
            height: cutout.height,
            borderRadius: 8,
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.65)',
            border: '1px solid rgba(33,138,255,0.6)',
            transition: 'top 250ms ease, left 250ms ease, width 250ms ease, height 250ms ease',
            pointerEvents: 'none',
          }}
        />
      )}

      {/* Per-element accent rings (multi-field steps: target + extraTargets) */}
      {!centered && rings.map((r, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            top: r.top - 3,
            left: r.left - 3,
            width: r.width + 6,
            height: r.height + 6,
            border: '2px solid #218aff',
            borderRadius: 6,
            boxShadow: '0 0 8px rgba(33,138,255,0.55)',
            transition: 'top 200ms ease, left 200ms ease, width 200ms ease, height 200ms ease',
            pointerEvents: 'none',
          }}
        />
      ))}

      {/* Tooltip card */}
      <div
        ref={cardRef}
        style={{
          position: 'absolute',
          width: cardMaxWidth,
          background: '#1a1a1a',
          border: '1px solid #333',
          borderTop: '3px solid #218aff',
          borderRadius: 10,
          padding: '16px 18px 14px',
          boxShadow: '0 8px 30px rgba(0,0,0,0.6)',
          color: '#e5e7eb',
          opacity: resolving ? 0.35 : 1,
          transition: 'top 250ms ease, left 250ms ease, opacity 150ms ease',
          ...cardStyle,
        }}
        role="dialog"
        aria-label={step.title}
      >
        <div style={{ fontSize: 15, fontWeight: 700, color: '#fff', marginBottom: 8 }}>
          {step.title}
        </div>
        {bodyLines.map((line, i) => (
          <p key={i} style={{ fontSize: 13, lineHeight: 1.55, color: '#c3c9d4', margin: i === 0 ? 0 : '8px 0 0' }}>
            {line}
          </p>
        ))}
        {choiceList.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 12 }}>
            {choiceList.map((choice) => (
              <button
                key={choice.key}
                type="button"
                onClick={() => handleChoice(choice)}
                disabled={resolving}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  background: choice.selected ? 'rgba(33,138,255,0.15)' : '#111827',
                  border: `1px solid ${choice.selected ? '#218aff' : '#333'}`,
                  borderRadius: 8,
                  padding: '8px 12px',
                  cursor: 'pointer',
                  transition: 'border-color 0.15s ease, background 0.15s ease',
                }}
              >
                <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: choice.selected ? '#218aff' : '#fff' }}>
                  {choice.label}
                </span>
                {choice.description && (
                  <span style={{ display: 'block', fontSize: 11.5, lineHeight: 1.4, color: '#8b93a2', marginTop: 2 }}>
                    {choice.description}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: 14,
            gap: 8,
          }}
        >
          <span style={{ fontSize: 11, color: '#6b7280', flexShrink: 0 }}>
            {index + 1} of {total}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              type="button"
              onClick={handleSkip}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#6b7280',
                fontSize: 12,
                cursor: 'pointer',
                padding: '4px 6px',
                textDecoration: 'underline',
              }}
            >
              Skip tour
            </button>
            {index > 0 && (
              <button
                type="button"
                onClick={handleBack}
                disabled={resolving}
                style={{
                  background: 'transparent',
                  border: '1px solid #444',
                  color: '#aeb9cc',
                  fontSize: 12,
                  fontWeight: 600,
                  padding: '5px 12px',
                  borderRadius: 6,
                  cursor: 'pointer',
                }}
              >
                Back
              </button>
            )}
            <button
              type="button"
              onClick={handleNext}
              disabled={resolving}
              style={{
                background: '#218aff',
                border: '1px solid #218aff',
                color: '#fff',
                fontSize: 12,
                fontWeight: 700,
                padding: '5px 14px',
                borderRadius: 6,
                cursor: 'pointer',
              }}
            >
              {step.nextLabel || (isLast ? 'Finish' : 'Next')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
